import { routeConstants } from 'app/routes/route-constants';
import { CaseState } from 'app/area/case/types-store';
import { ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import get from 'lodash/get';
import { sectionId, tableName } from 'app/area/case/case-data-model';
import { routeFromStoreItemPath } from 'app/infrastructure/store/entity-route';
import { RenderMode } from 'app/types-shared';
import { getById } from 'app/infrastructure/store/query';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';

export interface UrlOptions{
  withBaseUrl: boolean;
  openNew?: boolean;
}

/**
 * Injects a valid URL for the current connected case
 *
 *  @param config - `withBaseUrl:true` will include the virtual directory name when
 * hosted outside root.
 * @param state
 * @param uiEngineProps
 */
export const urlToConnectedCase = (config:UrlOptions = {withBaseUrl:false}) => (
  state: CaseState,
  uiEngineProps: ConnectedComponentProps
) => {
  const componentEntityType =
    uiEngineProps.componentDescriptor.entityTypePath &&
    uiEngineProps.componentDescriptor.entityTypePath.slice(-1)[0];
  
  if (componentEntityType && componentEntityType !== tableName.connectedCases) {
    return undefined;
  }

  if (!state.entities) {
    return undefined;
  }

  const casePk = uiEngineProps.hostProps().entityId;
  const idPath = uiEngineProps.resolveContext.path;
  
  // Workaround for connected cases in case listing
  const indexKey = state.entities[casePk] ? casePk : Object.keys(state.entities).find(key => Number(key.split('-')[0]) === casePk);
  
  const connectedCase = getById(
    state.editing ? state.editing.values : state.entities[indexKey],
    idPath
  );
  const caseNumber = get(connectedCase, 'ConnectedCaseNo[0].value');
  let url = caseNumber ? routeConstants.caseViewFor(caseNumber) : undefined;
  if(config.withBaseUrl && url){
    url = toAbsoluteUrl(url);
  }
  return {url, urlOpenNew: config.openNew};
};
/**
 * Injects a URL for the current case, regardless of read or edit mode
 *
 * @param state
 * @param uiEngineProps
 */
export const urlToCase = (config: UrlOptions = { withBaseUrl: false }) => (
  state: CaseState,
  uiEngineProps: ConnectedComponentProps
) => {
  if (uiEngineProps.url) {
    return undefined;
  }
  let url = routeConstants.caseViewFor(uiEngineProps.hostProps().caseNumber);
  if (config.withBaseUrl) {
    url = toAbsoluteUrl(url);
  }
  return { url, urlOpenNew: config.openNew };
};

const findByAttribute = (path: any[], attributeName: string, attributeValue: number | string) => {
  for (const node of path) {
    if (node[attributeName] === attributeValue) {
      return node.domainId;
    }
    if (node.children) {
      const result = findByAttribute(node.children, attributeName, attributeValue);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * Injects a deep link url for the entity specified by resolveContext and hostProps().caseNumber
 *
 * @param state
 * @param uiEngineProps
 */
export const urlToChildEntity =  (toSection: boolean = false) => (
  state: any,
  uiEngineProps: ConnectedComponentProps  
) => {
  const path = [...uiEngineProps.resolveContext.path];
  if (!path || !path.length) {
    return undefined;
  }
  const caseItem = { ...path[0] };
  caseItem.tblTrans = uiEngineProps.hostProps().caseNumber;
  path[0] = caseItem;
  //enable deep linking to section if needed e.g., in actions
  const tableItem = toSection && path[1] &&  Object.keys(path[1]).toString();
  const uiDefinition = state?.uiDefinitions?.[0]?.definition?.rootDomain?.children;
  const targetSection = sectionId[tableItem]?.section;
  const sectionDomainExists = findByAttribute(uiDefinition, 'domainId', targetSection);
  const section = !!tableItem && !!sectionDomainExists && targetSection;
  
  return { url: `${routeFromStoreItemPath(path, RenderMode.read)}${!!section ? ('?section=' + section) : ''}` };
};
